import { formatMoney } from '@mms/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, ListGroup, Row as BRow, Table } from 'react-bootstrap';
import { useSearchParams } from 'react-router';
import { ApiError, api, qs, type Params } from '../api/client';
import type { Option, Row } from '../api/types';
import { DataTable, type Column } from '../ui/DataTable';
import { DateRange, PRESETS, type Range } from '../ui/DateRange';
import { Amount, fmtDate } from '../ui/format';
import { BalanceSheetView, groupByCurrency } from './BalanceSheetView';
import { toOptions, useLookup } from '../ui/lookups';
import { PageHeader } from '../ui/PageHeader';

interface ReportInfo {
  code: string;
  group: string;
  name: string;
  filters: string[];
  /** true = current balances, no date range */
  snapshot: boolean;
  /** shown instead of a date-range picker when `snapshot` is true */
  snapshotNote?: string;
}
interface ReportColumn {
  key: string;
  label: string;
  type: 'text' | 'date' | 'money' | 'rate' | 'int';
  total?: boolean;
  /** for money/rate columns: another column's key, whose value is shown as a currency-code suffix inline */
  code?: string;
}
interface ReportResult {
  code: string;
  name: string;
  from?: string;
  to?: string;
  asOf?: string;
  snapshot: boolean;
  timezone: string;
  columns: ReportColumn[];
  /** every total-flagged column, including any hidden from `columns` itself (e.g. a Credit/Debit
   * split's single unsigned "Total") - drives the Totals-by-currency summary below the table. */
  totalColumns?: { key: string; label: string }[];
  rows: Row[];
  rowCount: number;
  totals?: Record<string, Record<string, string>>;
  /** running-balance summary for a single-entity filter (e.g. one agent): opening/today/final total -
   * or, when the report sets its own `footerLabels`, whatever that report's own shape is instead
   * (e.g. Cash Flow's Opening/Credit/Debit/Closing Balance). */
  footer?: { currencyCode?: string } & Record<string, string | undefined>;
  /** overrides which footer fields render, and their labels - see defs.ts `ReportDef.footerLabels`. */
  footerLabels?: Record<string, string>;
}

const FOOTER_LABELS: Record<string, string> = {
  openingBalance: 'Opening Balance', ordersAmount: 'Orders Amount', paymentReceived: 'Payment Received', total: 'Final Total',
};

const FILTERS: Record<string, { lookup: string; label: string; labelFn?: (r: Row) => string }> = {
  agentId: { lookup: 'agents', label: 'Agent' },
  supplierId: { lookup: 'suppliers', label: 'Dispatcher' },
  purchaserId: { lookup: 'purchasers', label: 'Seller' },
  bankId: { lookup: 'banks', label: 'Bank' },
  customerId: { lookup: 'customers', label: 'Customer' },
  categoryId: { lookup: 'expense-categories', label: 'Category' },
  // Sellers are multi-currency: the Seller Statement needs Currency too, to pick one of a seller's balances.
  currencyId: { lookup: 'currencies', label: 'Currency', labelFn: (r) => String(r.code) },
};

function FilterSelect({ name, value, onChange }: { name: string; value: string; onChange: (v: string) => void }) {
  const cfg = FILTERS[name]!;
  const list = useLookup(cfg.lookup);
  const options: Option[] = toOptions(list.data, cfg.labelFn);
  return (
    <Form.Group className="me-2 mb-2" style={{ minWidth: 180 }} controlId={`report-filter-${name}`}>
      <Form.Label className="small mb-1">{cfg.label}</Form.Label>
      <Form.Select size="sm" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Form.Select>
    </Form.Group>
  );
}

const cellFor = (c: ReportColumn): Column => ({
  key: c.key,
  header: c.label,
  align: c.type === 'money' || c.type === 'rate' || c.type === 'int' ? 'end' : undefined,
  className: c.type === 'date' ? 'text-nowrap' : undefined,
  render: (r) => {
    const v = r[c.key];
    if (v === null || v === undefined || v === '') return <span className="text-body-tertiary">—</span>;
    if (c.type === 'money') return <Amount value={v} code={c.code ? (r[c.code] as string | undefined) : undefined} />;
    if (c.type === 'rate') {
      const code = c.code ? (r[c.code] as string | undefined) : undefined;
      return (
        <span className="amount">
          {formatMoney(v, 2, 8)}
          {code ? <small className="text-body-secondary ms-1">{code}</small> : null}
        </span>
      );
    }
    if (c.type === 'date') return fmtDate(v);
    return String(v);
  },
});

const PAGE = 100;

export function ReportsPage() {
  const [sp, setSp] = useSearchParams();
  const code = sp.get('r') ?? '';
  const catalogue = useQuery({ queryKey: ['reports', 'catalogue'], queryFn: () => api.get<ReportInfo[]>('/reports'), staleTime: 300_000 });
  const info = catalogue.data?.find((r) => r.code === code);

  const [range, setRange] = useState<Range>(PRESETS[4]!.range()); // this month
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState<{ code: string; params: Params } | null>(null);
  const [page, setPage] = useState(1);

  const select = (c: string) => {
    setSp({ r: c });
    setFilters({});
    setSubmitted(null);
    setPage(1);
  };

  const run = useQuery({
    queryKey: ['report', submitted],
    queryFn: () => api.get<ReportResult>(`/reports/${submitted!.code}`, submitted!.params),
    enabled: !!submitted,
    staleTime: 0,
    gcTime: 60_000,
  });

  const groups = useMemo(() => {
    const m = new Map<string, ReportInfo[]>();
    for (const r of catalogue.data ?? []) m.set(r.group, [...(m.get(r.group) ?? []), r]);
    return [...m.entries()];
  }, [catalogue.data]);

  // Balance sheets show current balances, so they take no date range.
  const rangeValid = info?.snapshot || (!!range.from && !!range.to && range.from <= range.to);
  const doRun = () => {
    if (!info || !rangeValid) return;
    const params: Params = info.snapshot ? {} : { from: range.from, to: range.to };
    for (const f of info.filters) if (filters[f]) params[f] = filters[f];
    setPage(1);
    setSubmitted({ code: info.code, params });
  };

  const result = run.data;
  // The business-overview Balance Sheet is shown as one statement per currency instead of a flat table.
  const isBalanceSheet = result?.code === 'balance-sheet';
  const columns = useMemo(() => result?.columns.map(cellFor) ?? [], [result]);
  const pageRows = result?.rows.slice((page - 1) * PAGE, page * PAGE);
  const exportHref = (format: 'csv' | 'xlsx') => (submitted ? `/api/reports/${submitted.code}${qs({ ...submitted.params, format })}` : '#');

  return (
    <>
      <div className="no-print"><PageHeader title="Reports" icon="pie-chart" subtitle={info ? `${info.group} › ${info.name}` : 'Choose a report'} /></div>
      <BRow className="g-3">
        <Col lg={3} className="no-print">
          <Card>
            <Card.Body className="p-2">
              {catalogue.isPending && <div className="p-2 text-body-secondary">Loading…</div>}
              {groups.map(([group, list]) => (
                <div key={group} className="mb-2">
                  <div className="text-uppercase small text-body-secondary px-2 pt-2">{group}</div>
                  <ListGroup variant="flush">
                    {list.map((r) => (
                      <ListGroup.Item key={r.code} action active={r.code === code} onClick={() => select(r.code)}>
                        {r.name}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={9} className="mms-report-result">
          {!info ? (
            <Card><Card.Body className="text-center text-body-secondary py-5"><i className="bi bi-bar-chart-line display-5 d-block mb-2" aria-hidden />Select a report from the list.</Card.Body></Card>
          ) : (
            <>
              <Card className="mb-3 no-print">
                <Card.Body>
                  <div className="d-flex flex-wrap align-items-end">
                    {info.snapshot ? (
                      <div className="me-3 mb-2 text-body-secondary small align-self-center">{info.snapshotNote}</div>
                    ) : (
                      <Form.Group className="me-3 mb-2" role="group" aria-label="Date range">
                        <Form.Label as="div" className="small mb-1" aria-hidden>Date range</Form.Label>
                        <DateRange value={range} onChange={setRange} />
                      </Form.Group>
                    )}
                    {info.filters.map((f) => (
                      <FilterSelect key={f} name={f} value={filters[f] ?? ''} onChange={(v) => setFilters((s) => ({ ...s, [f]: v }))} />
                    ))}
                    <Button className="mb-2" onClick={doRun} disabled={!rangeValid || run.isFetching}>
                      <i className="bi bi-play-fill me-1" aria-hidden />{run.isFetching ? 'Running…' : 'Run report'}
                    </Button>
                  </div>
                  {!rangeValid && <div className="text-danger small">Choose a valid date range (from must not be after to).</div>}
                </Card.Body>
              </Card>

              {run.isError && <Alert variant="danger" className="no-print">{run.error instanceof ApiError ? run.error.message : 'The report failed to run'}</Alert>}

              {result && (
                <Card>
                  <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div>
                      <strong>{result.name}</strong>
                      <span className="text-body-secondary ms-2">
                        {result.snapshot ? `As of ${fmtDate(result.asOf)}` : `${fmtDate(result.from)} – ${fmtDate(result.to)}`} · {isBalanceSheet ? `${groupByCurrency(result.rows).length} currencies` : `${result.rowCount} rows`}
                      </span>
                    </div>
                    <div className="d-flex gap-2 no-print">
                      <a className="btn btn-sm btn-outline-secondary" href={exportHref('csv')} download><i className="bi bi-filetype-csv me-1" aria-hidden />CSV</a>
                      <a className="btn btn-sm btn-outline-success" href={exportHref('xlsx')} download><i className="bi bi-file-earmark-excel me-1" aria-hidden />Excel</a>
                      <Button size="sm" variant="outline-secondary" onClick={() => window.print()}><i className="bi bi-printer me-1" aria-hidden />Print</Button>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-2 p-md-3">
                    {isBalanceSheet ? (
                      <BalanceSheetView rows={result.rows} />
                    ) : (
                      <DataTable
                        columns={columns}
                        rows={pageRows}
                        rowKey={(r) => `${r.transactionNo ?? ''}-${result.rows.indexOf(r)}`}
                        page={page} pageSize={PAGE} total={result.rowCount} onPage={setPage}
                        empty="No records in this period"
                        caption={result.name}
                      />
                    )}
                    {result.totals && (
                      <Table size="sm" className="mt-3 mb-0 w-auto" aria-label="Totals by currency">
                        <thead><tr><th>Currency</th>{(result.totalColumns ?? []).map((c) => <th key={c.key} className="text-end">{/\btotal\b/i.test(c.label) ? c.label : `Total ${c.label}`}</th>)}</tr></thead>
                        <tbody>
                          {Object.entries(result.totals).map(([cur, sums]) => (
                            <tr key={cur} className="fw-semibold">
                              <td>{cur}</td>
                              {(result.totalColumns ?? []).map((c) => <td key={c.key} className="text-end"><Amount value={sums[c.key]} /></td>)}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                    {result.footer && (() => {
                      const labels = Object.entries(result.footerLabels ?? FOOTER_LABELS);
                      const lastKey = labels[labels.length - 1]?.[0];
                      return (
                        <Table size="sm" bordered className="mt-3 mb-0 w-auto" aria-label="Balance summary">
                          <tbody>
                            {labels.map(([key, label]) => (
                              <tr key={key} className={key === lastKey ? 'fw-bold' : undefined}>
                                <th scope="row" className="fw-normal text-body-secondary">{label}</th>
                                <td className="text-end"><Amount value={result.footer?.[key]} code={result.footer?.currencyCode} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      );
                    })()}
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Col>
      </BRow>
    </>
  );
}
